import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { IconButton, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import InputMask from "react-input-mask";
import { useHistory, useParams } from "react-router-dom";
import Select from "react-select";
import { Service } from "../../Service/ApiService";
import { CommonService } from "../../Service/CommonService";
import Spinner from "../Common/Spinner";
import "../Static/Login/Login.scss";
import "./AddCompanyOrUser.scss";
import useFormValidation from "./FormValidation";
import PasswordStrength from "./PasswordStrength";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from '@mui/icons-material/Warning';
import { useZipcodeValidation } from "../../hooks/useZipcodeValidation";
import Constant from "../../Constant";

interface AddEntityProps {
  type: "user" | "company";
}

const checkPasswordStrength = (password) => {
  if (password.length < 6) return "Weak";
  if (password.length < 10) return "Medium";
  return "Strong";
};

const AddCompanyOrUser: React.FC<AddEntityProps> = ({ type }) => {
  const {id} = useParams<{id: string}>();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [CompaniesList, setCompaniesList] = useState([]);
  const [filteredCompaniesList, setFilteredCompaniesList] = useState([]);
  const [lastSelectedCompany, setLastSelectedCompany] = useState<any>();
  const [rolesList, setRolesList] = useState([]);
  const [showPasswordFields, setShowPasswordFields] = useState("no");
  const [selected, setSelected] = useState("No");
  const [showPassword, setShowPassword] = useState(false);
  const [showRetypePassword, setShowRetypePassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState("");
  const [isValidZipcode, setValidZipcode] = useState(false);
  const [isZipcodeLoading, setZipcodeLoading] = useState(false);
  const [isEmailExist, setIsEmailExist] = useState(false);
  const [showAlert, setShowAlert] = useState(true);
  const allowedRoles = ["Super Admin", "Warehouse"];

  const initialFormData = {
    firstName: "",
    lastName: "",
    birthdate: "",
    zipcode: "",
    phoneNumber: "",
    email: "",
    password: "",
    retypePassword: "",
    companyName: "",
    companyEmail: "",
    contactNumber: "",
    contactPerson: "",
    companyId: CommonService.isAdmin() ? "" : CommonService.getCompanyId(),
    siteId: 1,
    roleId: "",
    userStatus: CommonService.isAdmin() ? "ACCEPTED" : "PENDING_REVIEW",
    city: "",
    state: "",
    role: "",
    streetAddress: "",
    isBroker: false,
  };

  const {
    formData,
    errors,
    handleChange,
    handleBlur,
    setErrors,
    validateForm,
    setFormData,
  } = useFormValidation(initialFormData);

  // integrate zipcode validation with the form states
  const { zipcodeLoading, validZipcode, validatePostalCode } = useZipcodeValidation(setFormData, setErrors);

  const fetchCompanyDetails = async (id:any) => {
    try {
      const response = await Service.getCompanyDetailsById(id);
      const companyData = response?.data;

      if (companyData) {
        setFormData((prevData) => ({
          ...prevData,
          companyId: companyData.id || "",
          companyEmail: companyData.email || "",
          contactNumber: companyData.contactNumber || "",
          contactPerson: companyData.contactPerson || "",
          companyName: companyData.companyName || "",
        }));
      }

    } catch(error) {
      console.log("Error occurred while fetching company details", error);
    }
  }

  useEffect(() => {

    if(id) {
      fetchCompanyDetails(id);
    }
    
    if(type == "user") {
      fetchExistingCompanies();
      fetchRoles();
    }
    
  }, []);

  const fetchExistingCompanies = async () => {
    try {
      const response = await Service.fetchCompaniesList();

      setCompaniesList(response.data);
       // Set companies list and, if logged-in user is SUPER_ADMIN 
      // and WDTP is not already present, add WDTP company entry
      setFilteredCompaniesList([
        ...response.data,
        { id: CommonService.getCompanyId(), companyName: CommonService.getCompanyName() }
      ]);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await Service.getUserRoles(CommonService.isAdmin() ? true : false); // includeSuperAdmin or not
      setRolesList(response.data);
    } catch (error) {
      console.error("Error while fetching the roles ", error);
    }
  }

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Toggle confirm password visibility
  const toggleConfirmPasswordVisibility = () => {
    setShowRetypePassword(!showRetypePassword);
  };

  const validateZipcode = async (zipcode: string): Promise<boolean> => {
    return await validatePostalCode(zipcode);
  };

  const handleZipcodeBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    setZipcodeLoading(true);
    const zipcode = e.target.value;
    await validateZipcode(zipcode);
  };

  const checkEmailExistOrNot = async (e: React.FocusEvent<HTMLInputElement>) => {
    try{

      if(e.target.value === '')
        return;
      
      const response = await Service.checkEmailExistOrNot(e.target.value);
      setIsEmailExist(response.data);
      setShowAlert(true)
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        email: error.response.data.errorMessage,
      }));
      CommonService.Toast.fire({
        title: error.response.data.errorMessage,
        icon: "error",
      });
      return false;
    } 
  };

  const routeBack = () => {
    history.goBack();
  };

  // Validate user fields
  const validateUserFields = async (): Promise<boolean> => {
    let isValidationPass = true;

    const requiredFields = [
      { field: "firstName", error: "Please enter first name" },
      { field: "birthdate", error: "Please enter birthdate" },
      { field: "email", error: "Please enter email" },
      { field: "role", error: "Please select user role"}
    ];

    if (CommonService.isAdmin()) {
      requiredFields.push({
        field: "companyName",
        error: "Please select company name",
      });
    }

    

    requiredFields.forEach(({ field, error }) => {
      if (formData[field].trim().length === 0 || errors[field]) {
        if (formData[field].trim().length === 0) {
          setErrors((prevState) => ({ ...prevState, [field]: error }));
        }
        isValidationPass = false;
      }
    });

    if (showPasswordFields === "yes") {
      if (formData.password.trim().length === 0 || errors.password) {
        if (formData.password.trim().length === 0) {
          setErrors((prevState) => ({
            ...prevState,
            password: "Please enter password",
          }));
        } else if (
          formData.password.trim().length > 0 &&
          formData.password.trim().length < 6
        ) {
          setErrors((prevState) => ({
            ...prevState,
            password: "Password must be at least 6 characters long",
          }));
        }
        isValidationPass = false;
      }

      if (
        formData.retypePassword.length === 0 ||
        formData.retypePassword !== formData.password
      ) {
        setErrors((prevState) => ({
          ...prevState,
          retypePassword:
            formData.retypePassword.length === 0
              ? "Please retype your password"
              : "Passwords do not match",
        }));
        isValidationPass = false;
      }
    } else {
      setFormData((prevState) => ({
        ...prevState,
        password: "",
        retypePassword: "",
      }));
    }

    if (formData.zipcode.length === 0 || errors.zipcode) {
      if (formData.zipcode.length === 0) {
        setErrors((prevState) => ({
          ...prevState,
          zipcode: "Please enter zipcode",
        }));
      }
      isValidationPass = false;
    } else {
      const result = await validateZipcode(formData.zipcode.trim());
      if(!result) {
        isValidationPass = false;
      }
    }

    return isValidationPass;
  };

  const companyOptions = CompaniesList.map((company: any) => ({
    value: company.id,
    label: company.companyName,
  }));

  const filteredCompanyOptions = filteredCompaniesList.map((company: any) => ({
    value: company.id,
    label: company.companyName,
  }));

  const dateInputRef = useRef(null);

  const handleDateClick = () => {
    if (dateInputRef.current) {
      dateInputRef.current?.showPicker(); // This will trigger the date picker to open
    }
  };

  // Validate company fields
  const validateCompanyFields = async (): Promise<boolean> => {
    let isValidationPass = true;

    const requiredFields = [
      { field: "companyName", error: "Please enter company name" },
      { field: "companyEmail", error: "Please enter company email" },
    ];

    requiredFields.forEach(({ field, error }) => {
      if (formData[field].trim().length === 0 || errors[field]) {
        if (formData[field].trim().length === 0) {
          setErrors((prevState) => ({ ...prevState, [field]: error }));
        }
        isValidationPass = false;
      }
    });

    return isValidationPass;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const isFormValid =
      type === "user"
        ? await validateUserFields()
        : await validateCompanyFields();

    if (!isFormValid) {
      setIsLoading(false);
      return;
    }


    try {
      console.log(formData);
      let response;
      if (type === "user") {
        response = await Service.addUser(formData);
      } else {
        response = await Service.addCompany(formData);
      }

      if (response.data !== 'EMAIL_EXISTS' && response.data !== 'COMPANY_EXISTS') {
        if(CommonService.isAdmin()){
          CommonService.Toast.fire({
            title:
              type === "user"
                ? "User Added Successfully"
                : (id ? "Company Details Updated Successfully " : "Company Added Successfully"),
            icon: "success",
          });
          history.push(
            type === "user" ? "/master/user-list" : "/master/company-list"
          );
        }else{
          CommonService.Toast.fire({
            title:"Your request is successfully sent to Admin",
            icon: "success"
          });
          history.push("/user/user-list");          
        }
      } else {
        if (response.data === "EMAIL_EXISTS") {
          CommonService.Toast.fire({
            title: "This Email already exists",
            icon: "error",
          });
        } else if (response.data === "COMPANY_EXISTS") {
            CommonService.Toast.fire({
                title: "This Company Name already exists",
                icon: "error",
            });
        }
      }
    } catch (error: any) {

      if(type == "user") {
        CommonService.Toast.fire({
          title: error.response.data.errorMessage,
          icon: "error",
        });


        setErrors((prev) => ({ ...prev, email: error.response.data.errorMessage }));
      } else {

         if(error?.response?.data?.errorMessage === "EMAIL_EXISTS") {
          CommonService.Toast.fire({
            title: 'This Email already exists',
            icon: "error",
          });
        } else if(error?.response?.data?.errorMessage === "COMPANY_EXISTS" ) {
          CommonService.Toast.fire({
            title: 'This Company already exists',
            icon: "error",
          });
        }
        else {
          CommonService.Toast.fire({
            title: error.response.data.error,
            icon: "error",
          });
        }
      }
      
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompanySelect = (id: string) => {
    const selectedCompany = CompaniesList.find((company) => company.id === id);
    setLastSelectedCompany({ id: selectedCompany.id, name: selectedCompany.companyName });
    if (selectedCompany) {
      setErrors((prevErrors) => ({ ...prevErrors, companyName: "", email: "" }));
      setFormData((prevState) => ({
        ...prevState,
        companyName: selectedCompany.companyName,
        siteId: selectedCompany.siteId,
        companyId: selectedCompany.id,
      }));
    }
  };

  const handleRoleSelect = (id: string) => {
    const selectedRole = rolesList.find((userRole) => userRole.id === id);
    if (selectedRole) {
      setErrors((prevErrors) => ({ ...prevErrors, role: "" }));

      let companyData = {};
      if (CommonService.isAdmin()) {
        companyData = (selectedRole.roleName === Constant.Role.SUPER_ADMIN || selectedRole.roleName === Constant.Role.WAREHOUSE)
          ? {
              companyId: CommonService.getCompanyId(),
              companyName: CommonService.getCompanyName(),
            }
          : {
              // restore last selected company if exists
              companyId: lastSelectedCompany?.id || "",
              companyName: lastSelectedCompany?.name || "",
            }
      }

      setFormData((prevState) => ({
        ...prevState,
        roleId: selectedRole.id,
        role: CommonService.formatRoleName(selectedRole.roleName),
        ...companyData,
      }));
    }
  };

  const handleBarrelSelect = (flag: string) => {
    console.log(flag);

    if (flag) {
      setFormData((prevState) => ({
        ...prevState,
        isBroker: flag == "yes" ? true : false,
      }));
    }
  };

  const handleTogglePasswordFields = (value: "yes" | "no") => {
    setShowPasswordFields(value);

    // If "no" is selected, clear password and retypePassword fields  
    if (value === "no") {
      setFormData((prevData) => ({
        ...prevData,
        password: "",
        retypePassword: "",
      }));
    }
  };

  const getMinAgeDate = (): string => {
    const today = new Date();
    const minAgeYear = today.getFullYear() - 21;
    return `${minAgeYear}-${String(today.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(today.getDate()).padStart(2, "0")}`;
  };

  const minAgeDate = getMinAgeDate();

  return (
    <>
      <div className="add-company-user-wrapper">
        <div className="d-flex align-items-center mb-3 px-3">
          {CommonService.isAdmin() && (
            <>
            <div className="back__button" onClick={routeBack}>
              <IconButton
                aria-label="ArrowBackIcon"
                sx={{ backgroundColor: "#FFF" }}
              >
                <ArrowBackIcon sx={{ color: "#1C1B1F" }} />
              </IconButton>
            </div>
          
          <div className="title">
            <Typography
              variant="h1"
              component="h2"
              sx={{ ml: 2, fontWeight: 600, fontSize: "25px" }}
            >
              {type === "user" ? "Add User" : (id ? "Update Company" : "Add Company")}
            </Typography>
          </div>
          </>
          )}
        </div>
        <div className="add-company-user-form-wrapper p-0">
          <form
            className="form__inner row d-flex mx-auto py-5"
            onSubmit={onSubmit}
            noValidate
            onKeyDown={e => e.key === 'Enter' && e.preventDefault()}
          >
             
            {type === "user" && (
              
              <>
              {
                (isEmailExist && showAlert) &&(
                  <div className="col-12">
                    <div className="alert barrel-alert user-exist-alert alert-dismissible fade show" role="alert">
                      <span className="d-flex align-items-center gap-2">
                        <WarningIcon />Email already exists. Existing password will be reused.</span>
                      <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowAlert(false)}> <CloseIcon /></button>
                    </div>
                  </div>
                  )
              }
                <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                  <label htmlFor="firstName" className="form-label">
                    First Name{" "}
                    <span className="star-required text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    placeholder="Enter first name"
                    value={formData.firstName}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^A-Za-z ]/g, "");
                      handleChange({
                        target: {
                          name: e.target.name,
                          value: value,
                        },
                      });
                    }}
                    onBlur={handleBlur}
                    className={`form-control ${
                      errors.firstName ? "is_invalid" : ""
                    }`}
                  />
                  {errors.firstName && (
                    <div className="error-message">{errors.firstName}</div>
                  )}
                </div>
                <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                  <label htmlFor="lastName" className="form-label">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    placeholder="Enter last name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^A-Za-z ]/g, "");
                      handleChange({
                        target: {
                          name: e.target.name,
                          value: value,
                        },
                      });
                    }}
                    className="form-control"
                  />
                </div>
                <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                  <label htmlFor="birthdate" className="form-label">
                    Birthdate (Must be 21+ years){" "}
                    <span className="star-required text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    id="birthdate"
                    name="birthdate"
                    placeholder="Birthdate"
                    value={formData.birthdate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    max={minAgeDate}
                    // ref={dateInputRef}
                    onClick={handleDateClick}
                    className={`form-control ${
                      errors.birthdate ? "is_invalid" : ""
                    }`}
                  />
                  {errors.birthdate && (
                    <div className="error-message">{errors.birthdate}</div>
                  )}
                </div>
                <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                  <label htmlFor="zipcode" className="form-label">
                    Zip Code{" "}
                    <span className="star-required text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="zipcode"
                    name="zipcode"
                    placeholder="Enter zipcode"
                    value={formData.zipcode}
                    onChange={handleChange}
                    onBlur={handleZipcodeBlur}
                    className={`form-control ${
                      errors.zipcode ? "is_invalid" : ""
                    }`}
                  />
                  {errors.zipcode && (
                    <div className="error-message">{errors.zipcode}</div>
                  )}
                  {formData.city && formData.state && (
                    <p className="mt-1 mb-0 zip-code" style={{fontSize: '12px'}}>
                      {formData.city + ", " + formData.state}
                    </p>
                  )}
                </div>

                <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                  <label htmlFor="email" className="form-label">
                    Email Address{" "}
                    <span className="star-required text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Enter email ID"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={(e) => {
                      handleBlur(e);
                      checkEmailExistOrNot(e);
                    }}
                    className={`form-control ${
                      errors.email ? "is_invalid" : ""
                    }`}
                  />
                  {errors.email && (
                    <div className="error-message">{errors.email}</div>
                  )}
                </div>
                <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                  <label htmlFor="phoneNumber" className="form-label">
                    Phone Number
                  </label>
                  <InputMask
                    mask="(999) 999-9999"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                  >
                    {(inputProps) => (
                      <input
                        type="text"
                        id="phoneNumber"
                        name="phoneNumber"
                        placeholder="Enter phone number"
                        className="form-control"
                        {...inputProps}
                      />
                    )}
                  </InputMask>
                </div>

                <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                  <label htmlFor="streetAddress" className="form-label">
                    Street Address
                  </label>
                  <input
                    type="text"
                    id="streetAddress"
                    name="streetAddress"
                    placeholder="Enter street address"
                    value={formData.streetAddress}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="form-control"
                  />
                </div>

                {CommonService.isAdmin() && (
                  <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                    <label htmlFor="companyName" className="form-label">
                      Select Company{" "}
                      <span className="star-required text-danger">*</span>
                    </label>
                    {/* <Dropdown
                      className={`select-company-dropdown from-control w-100 ${
                        errors.companyName ? "is_invalid" : ""
                      }`}
                      onSelect={(id) => {
                        // handleChange({
                        //   target: {
                        //     name: "companyName",
                        //     value: id,
                        //   },
                        // });
                        handleCompanySelect(id);
                      }}
                    >
                      <Dropdown.Toggle
                        variant="none"
                        id="dropdown-basic"
                        className={`d-flex justify-content-between text-start border border-light-subtle bg-white w-100 ${
                          errors.companyName ? "is_invalid" : ""
                        }`}
                      >
                        {formData.companyName || "Select Company"}
                        <ExpandMoreRoundedIcon />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {CompaniesList.map((company: any) => (
                          <Dropdown.Item key={company.id} eventKey={company.id}>
                            {company.companyName}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown> */}

                    <Select
                      className={`select-company-dropdown form-control w-100 multi-select-dropdown ${
                        errors.companyName ? "is_invalid" : ""
                      }`}
                      options={allowedRoles.includes(formData.role) ? filteredCompanyOptions : companyOptions}
                      value={(allowedRoles.includes(formData.role) ? filteredCompanyOptions : companyOptions).find((option) => option.value === formData.companyId)}
                      onChange={(selectedOption) => {handleCompanySelect(selectedOption?.value);}}
                      components={{
                        DropdownIndicator: () => <ExpandMoreRoundedIcon />,
                        IndicatorSeparator: () => null, // Remove the default separator
                      }}
                      isSearchable
                      placeholder="Select Company"
                      isDisabled={allowedRoles.includes(formData.role)} 
                    />
                    {errors.companyName && (
                      <div className="error-message">{errors.companyName}</div>
                    )}
                  </div>
                )}

                <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                  <label htmlFor="role" className="form-label">
                    Select User Role{" "}
                    <span className="star-required text-danger">*</span>
                  </label>
                  <Dropdown
                    className={`select-company-dropdown from-control w-100 ${
                      errors.role ? "is_invalid" : ""
                    }`}
                    onSelect={handleRoleSelect}
                  >
                    <Dropdown.Toggle
                      variant="none"
                      id="dropdown-basic"
                      className={`d-flex justify-content-between text-start border border-light-subtle bg-white w-100 ${
                        errors.role ? "is_invalid" : ""
                      }`}
                    >
                      {formData.role || "Select user role"}
                      <ExpandMoreRoundedIcon />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {rolesList.map((userRole) => (
                        <Dropdown.Item key={userRole.id} eventKey={userRole.id}>
                          {CommonService.formatRoleName(userRole.roleName)}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                  {errors.role && (
                    <div className="error-message">{errors.role}</div>
                  )}
                </div>

                <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                  <label htmlFor="barrelBroker" className="form-label">
                    Is user Container Broker?
                  </label>
                  <Dropdown
                    className="select-company-dropdown w-100"
                    onSelect={(eventKey) => {
                      setSelected(eventKey);
                      handleChange({
                        target: { name: "barrelBroker", value: eventKey },
                      });
                      handleBarrelSelect(selected);
                    }}
                  >
                    <Dropdown.Toggle
                      variant="none"
                      id="barrel-broker-dropdown"
                      className="d-flex justify-content-between text-start border border-light-subtle bg-white w-100"
                    >
                      {selected || "Select Option"}
                      <ExpandMoreRoundedIcon />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item eventKey="Yes">Yes</Dropdown.Item>
                      <Dropdown.Item eventKey="No">No</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </>
            )}

            {type === "company" && (
              <>
                <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                  <label htmlFor="companyName" className="form-label">
                    Company Name{" "}
                    <span className="star-required text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    placeholder="Enter company name"
                    onChange={id ? undefined : handleChange}
                    onBlur={id ? undefined : handleBlur}
                    disabled= {id? true: false}
                    className={`form-control ${
                      errors.companyName ? "is_invalid" : ""
                    }`}
                  />
                  {errors.companyName && (
                    <div className="error-message">{errors.companyName}</div>
                  )}
                </div>
                <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                  <label htmlFor="companyEmail" className="form-label">
                    Company Email{" "}
                    <span className="star-required text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    id="companyEmail"
                    name="companyEmail"
                    placeholder="Enter company email"
                    value={formData.companyEmail}
                    onChange={id ? undefined : handleChange}
                    onBlur={id ? undefined : handleBlur}
                    disabled= {id? true: false}
                    className={`form-control ${
                      errors.companyEmail ? "is_invalid" : ""
                    }`}
                  />
                  {errors.companyEmail && (
                    <div className="error-message">{errors.companyEmail}</div>
                  )}
                </div>
                <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                  <label htmlFor="contactNumber" className="form-label">
                    Contact Number
                  </label>
                  <InputMask
                    mask="(999) 999-9999"
                    value={formData.contactNumber}
                    onChange={handleChange}
                  >
                    {(inputProps) => (
                      <input
                        type="text"
                        id="contactNumber"
                        name="contactNumber"
                        value={formData.contactNumber}
                        placeholder="Enter company contact number"
                        className="form-control"
                        {...inputProps}
                      />
                    )}
                  </InputMask>
                </div>

                <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                  <label htmlFor="contactPerson" className="form-label">
                    Contact Person
                  </label>
                  <input
                    type="text"
                    id="contactPerson"
                    name="contactPerson"
                    value={formData.contactPerson}
                    placeholder="Enter company contact person"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="form-control"
                    // className={`form-control ${errors.contactPerson ? "is_invalid" : ""}`}
                  />
                  {/* {errors.contactPerson && <div className="error-message">{errors.contactPerson}</div>} */}
                </div>
              </>
            )}

            {type === "user" && (
              !isEmailExist ? (
              <>
                <div className="col-12 mb-3 add-user-form-group">
                  <div className="form-check ps-0">
                    <label
                      className="form-check-label position-relative my-3 w-100"
                      htmlFor="flexSwitchCheckDefault"
                    >
                      <span className="d-inline-block position-relative pe-3 py-2 bg-white z-1">
                        Set Password
                      </span>
                      <hr className="position-absolute top-50 m-0 w-100 z-0" />
                    </label>
                    <div className="d-flex align-items-center">
                      <div className="d-inline-flex">
                        <input
                          type="radio"
                          name="setPassword"
                          id="setPassword2"
                          className="custom-radio"
                          value="no"
                          checked={showPasswordFields === "no"}
                          onChange={() => handleTogglePasswordFields("no")}
                        />
                        <label
                          className="custom-radio-label"
                          htmlFor="setPassword2"
                        >
                          No
                        </label>
                      </div>
                      <div className="d-inline-flex ms-4 me-2">
                        <input
                          type="radio"
                          name="setPassword"
                          id="setPassword1"
                          className="custom-radio"
                          value="yes"
                          checked={showPasswordFields === "yes"}
                          onChange={() => handleTogglePasswordFields("yes")}
                        />
                        <label
                          className="custom-radio-label"
                          htmlFor="setPassword1"
                        >
                          Yes
                        </label>
                      </div>
                      <label
                        className="form-check-label ms-2"
                        htmlFor="flexSwitchCheckDefault"
                      >
                        <InfoOutlinedIcon
                          fontSize="small"
                          sx={{ color: "#525E9D", mr: 1 }}
                        />
                        {showPasswordFields === "no" && (
                          <Typography
                            variant="body2"
                            sx={{
                              display: "inline",
                              color: "#737376",
                              fontSize: "15px",
                            }}
                          >
                            An email will be sent to verify and set password
                          </Typography>
                        )}
                        {showPasswordFields === "yes" && (
                          <Typography
                            variant="body2"
                            sx={{
                              display: "inline",
                              color: "#737376",
                              fontSize: "15px",
                            }}
                          >
                            This account will be automatically verified
                          </Typography>
                        )}
                      </label>
                    </div>
                  </div>
                </div>

                {showPasswordFields === "yes" && (
                  <>
                    <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                      <label htmlFor="password" className="form-label">
                        Enter Password{" "}
                        <span className="star-required text-danger">*</span>
                      </label>
                      <div className="position-relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={(e) => {
                            handleChange(e);
                            setPasswordStrength(
                              checkPasswordStrength(e.target.value)
                            );
                          }}
                          placeholder="Password"
                          onBlur={handleBlur}
                          className={`form-control ${
                            errors.password ? "is_invalid" : ""
                          }`}
                        />
                        <button
                          className="btn text-secondary position-absolute top-50 end-0 translate-middle-y"
                          type="button"
                          onClick={togglePasswordVisibility}
                        >
                          {showPassword ? <FaEye /> : <FaEyeSlash />}
                        </button>
                      </div>
                      {formData.password && (
                        <PasswordStrength password={formData.password} />
                      )}
                      {/* {passwordStrength && (
                                        <div className={`mt-1 text-${passwordStrength.toLowerCase()}`}>
                                            Strength: {passwordStrength}
                                        </div>
                                    )} */}
                      {errors.password && (
                        <div className="error-message">{errors.password}</div>
                      )}
                    </div>

                    <div className="col-12 col-sm-6 mb-3 add-user-form-group">
                      <label htmlFor="retypePassword" className="form-label">
                        Re-Enter Password{" "}
                        <span className="star-required text-danger">*</span>
                      </label>
                      <div className="position-relative">
                        <input
                          type={showRetypePassword ? "text" : "password"}
                          id="retypePassword"
                          name="retypePassword"
                          placeholder="Re-type password"
                          value={formData.retypePassword}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`form-control ${
                            errors.retypePassword &&
                            (formData.retypePassword !== formData.password ||
                              !formData.password ||
                              !formData.retypePassword)
                              ? "is_invalid"
                              : ""
                          }`}
                        />
                        <button
                          className="btn text-secondary position-absolute top-50 end-0 translate-middle-y"
                          type="button"
                          onClick={toggleConfirmPasswordVisibility}
                        >
                          {showRetypePassword ? <FaEye /> : <FaEyeSlash />}
                        </button>
                      </div>
                      {formData.retypePassword === formData.password &&
                        formData.password &&
                        formData.retypePassword && (
                          <div className="mt-1 text-success">Matched</div>
                        )}
                      {errors.retypePassword &&
                        (formData.retypePassword !== formData.password ||
                          !formData.password ||
                          !formData.retypePassword) && (
                          <div className="error-message">
                            {errors.retypePassword}
                          </div>
                        )}
                    </div>
                  </>
                )}
              </>
              ) : (
              // <label>
              //         Email already exists. Existing password will be reused.
              //     </label>
              <>
              {/* {
                showAlert &&(
                  <div className="col-12">
                    <div className="alert barrel-alert alert-dismissible fade show" role="alert">
                      <span className="d-flex align-items-center gap-2">
                        <WarningIcon />Email already exists. Existing password will be reused.</span>
                      <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowAlert(false)}> <CloseIcon /></button>
                    </div>
                  </div>
                  )
              } */}
              </>
              )              
            )}

            <div className="col-12 mb-3">
              <div className="form__btn--group row d-flex">
                <div className="col-auto pe-1">
                  <button
                    type="reset"
                    onClick={routeBack}
                    className="btn secondary-btn register-btn mb-2"
                  >
                    Cancel
                  </button>
                </div>
                <div className="col-auto ps-1">
                  <button
                    type="submit"
                    className="btn primary-btn register-btn mb-2"
                    disabled={isLoading}
                  >
                    {isLoading && <Spinner />}
                    {type === "user"
                      ? CommonService.isAdmin()
                        ? "Add user"
                        : "Submit Request"
                      : (id ? "Update" : "Add Company")}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddCompanyOrUser;
